import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { IPatientMedicationsResponse } from "../../../../@types/Patient";
import { ReactComponent as NoMedicationsImage } from "../../../../assets/images/man-woman-phone.svg";
import { InfiniteTable } from "../../../../components/InfiniteTable";
import { RetryButton } from "../../../../components/RetryButton";
import { Table } from "../../../../components/Table";
import { TableSkeleton } from "../../../../components/TableSkeleton";
import { buildDesktopTableColumns } from "../MedicationsSection/patientMedicationsTableColumns";
import {
  ImageContainer,
  MedicationsTableContainer,
  MedicationsTableInnerContainer,
  NoContentMessage,
} from "./PatientMedicationsTable.style";

type PatientMedicationsTableProps = {
  content: IPatientMedicationsResponse[];
  isLoading?: boolean;
  isError?: boolean;
  highlightedRows: boolean[];
  refreshTable: () => void;
  onRowClick?: (rowContent: IPatientMedicationsResponse) => void;
  // EN-5078: wards with EnablePatientAddMedication have no acceptance workflow —
  // those columns are swapped for the structured schedule's Times column.
  hideAcceptedAndDosage?: boolean;
  // Infinite scroll props (optional for backward compatibility)
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  useInfiniteScroll?: boolean;
};

export function PatientMedicationsTable({
  isLoading,
  isError,
  content,
  highlightedRows,
  refreshTable,
  onRowClick,
  hideAcceptedAndDosage = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  useInfiniteScroll = false,
}: PatientMedicationsTableProps) {
  const { t } = useTranslation();

  const columns = useMemo(
    () => buildDesktopTableColumns(hideAcceptedAndDosage),
    [hideAcceptedAndDosage]
  );

  if (isLoading) {
    return (
      <MedicationsTableContainer style={{ overflow: "hidden" }}>
        <MedicationsTableInnerContainer style={{ overflow: "hidden" }}>
          <TableSkeleton columnsNumber={4} rowsNumber={8} />
        </MedicationsTableInnerContainer>
      </MedicationsTableContainer>
    );
  }

  if (isError) {
    return (
      <ImageContainer>
        <p>
          <Trans
            i18nKey="patientPage_medications_loadError"
            components={{
              retry: <RetryButton onClick={refreshTable} />,
            }}
          />
        </p>
      </ImageContainer>
    );
  }

  if (content && content.length > 0) {
    // Use InfiniteTable when infinite scroll is enabled
    if (useInfiniteScroll && fetchNextPage) {
      return (
        <MedicationsTableContainer>
          <MedicationsTableInnerContainer>
            <InfiniteTable
              data={content}
              columns={columns}
              highlightedRows={highlightedRows}
              highlightColor="rgba(233, 230, 244, .48)"
              onRowClick={onRowClick}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              skeletonColumns={4}
              skeletonRows={8}
            />
          </MedicationsTableInnerContainer>
        </MedicationsTableContainer>
      );
    }

    // Default to regular Table for backward compatibility
    return (
      <MedicationsTableContainer>
        <MedicationsTableInnerContainer>
          <Table
            data={content}
            columns={columns}
            highlightedRows={highlightedRows}
            highlightColor="rgba(233, 230, 244, .48)"
            onRowClick={onRowClick}
          />
        </MedicationsTableInnerContainer>
      </MedicationsTableContainer>
    );
  }

  return (
    <ImageContainer>
      <NoMedicationsImage width={181} height={158} />
      <NoContentMessage>{t("patientPage_medication_empty")}</NoContentMessage>
    </ImageContainer>
  );
}
